import { TitleButton } from '@/common/buttons/TitleButton';

import { useTab } from '@/common/entityFramework/entityTabs/TabContext';
import { useNavState } from '@/common/navState/NavStateContext';
import { useEffect, useState } from 'react';

/**
 * Takes an array of FilterButtonSpecs and provides default functionality for Perspective Filter Buttons
 * A FilterButtonSpec has the following properties:
 * - key: a string that is the key for this filterButton
 * - icon: the icon of the button
 * - colors: an array of strings defining the color of the button at each stage
 * - toolTips: an array of strings defining the toolTip of the button at each stage
 * - numStages: an optional int defining how many stages this filterButton has
 *
 * returns:
 * - filterButtonStages: an object of key to current stage of each filterButton
 * - filterButtons: an array of FilterButtons that can be passed to augButtons
 * - onViewReset: a function that can be passed directly to EntityPerspective, or composed with other logic if there is more cleanup logic in this Perspective
 */
export const useFilterButtons = (filterButtonSpecs) => {
  const { navState, assign } = useNavState();
  const { tabId } = useTab();

  const navStateId = tabId ? tabId + 'FilterButtons' : 'filterButtons';

  const [filterButtonStages, setFilterButtonStages] = useState(() => {
    // Initialize all button stages to 0
    let filterButtonStages = {};
    filterButtonSpecs.forEach((filterButtonSpec) => {
      filterButtonStages[filterButtonSpec.key] = 0;
    });

    // If possible, restore navState for some (navigation scenario) or all (refresh scenario) filterButtons
    if (navState[navStateId]) {
      filterButtonStages = { ...filterButtonStages, ...navState[navStateId] };
    }

    return filterButtonStages;
  });

  useEffect(() => {
    assign(navStateId, filterButtonStages);
  }, [filterButtonStages]);

  const filterButtons = filterButtonSpecs.map((filterButtonSpec) => {
    const key = filterButtonSpec.key;
    const stage = filterButtonStages[key];

    return (
      <TitleButton
        key={key}
        icon={filterButtonSpec.icon}
        color={filterButtonSpec.colors[stage]}
        toolTip={filterButtonSpec.toolTips[stage]}
        onClick={() => {
          setFilterButtonStages((prev) => ({ ...prev, [key]: (prev[key] + 1) % (filterButtonSpec.numStages ?? 3) }));
        }}
      />
    );
  });

  const onViewReset = () => {
    setFilterButtonStages((prev) =>
      Object.fromEntries(Object.keys(prev).map((filterButtonKey) => [filterButtonKey, 0])),
    );
  };

  return {
    filterButtonStages,
    filterButtons,
    onViewReset,
  };
};
